import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import getSymbolFromCurrency from "currency-symbol-map";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import Spinner from "../../components/Spinner/Spinner";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import {
  createRegistrationAddOnCheckout,
  MyRegistration,
  RegistrationCatalog,
} from "../../api/registration";

const primaryBtnClass =
  "eh-btn-primary block w-full py-3 text-sm font-semibold text-center disabled:opacity-60 disabled:cursor-not-allowed";

function fieldClass(hasError: boolean): string {
  return `mt-1 w-full border p-2 ${
    hasError ? "border-red-500 bg-red-50" : "border-slate-300"
  }`;
}

interface Props {
  registration: MyRegistration;
  catalog: RegistrationCatalog;
  eventSlug: string;
}

export default function ConferenceAddOnForm({
  registration,
  catalog,
  eventSlug,
}: Props) {
  const [banquetQuantity, setBanquetQuantity] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currency = catalog.banquet?.currency.toUpperCase() ?? "USD";
  const symbol = getSymbolFromCurrency(currency) ?? "$";

  const banquetTotalPence = banquetQuantity * (catalog.banquet?.pricePence ?? 0);
  const totalPence = banquetTotalPence;

  const canSubmit = banquetQuantity > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    setIsSubmitting(true);
    try {
      const origin = window.location.origin;
      const result = await createRegistrationAddOnCheckout(
        registration.registrationId,
        {
          banquetQuantity,
          successUrl: `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}/checkout/cancelled`,
        },
      );
      window.location.href = result.checkoutUrl;
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Unable to start checkout."));
      setIsSubmitting(false);
    }
  }

  return (
    <div className="max-w-[640px] mx-auto py-12 px-4">
      <Helmet>
        <title>Add to registration | {catalog.event.title}</title>
      </Helmet>

      {isSubmitting && (
        <div className="fixed inset-0 z-50 bg-white/90 flex items-center justify-center">
          <div className="py-12 text-center">
            <Spinner />
            <p className="mt-4 text-sm text-slate-600">
              Redirecting you to Stripe. Please do not close this window.
            </p>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold mb-2">Add to your registration</h1>
      <p className="text-slate-600 mb-8">
        You&apos;re already registered for {catalog.event.title} (
        {registration.itemOrdered}).{" "}
        {catalog.banquet
          ? "You can purchase banquet seats below."
          : "Unfortunately, banquet tickets are not available anymore."}
      </p>

      <form onSubmit={handleSubmit} className="space-y-8">
        {catalog.banquet && (
          <div className="border border-slate-200 p-4">
            <h2 className="font-semibold mb-3">Add banquet seats</h2>
            <label className="block">
              <span className="text-sm font-medium">
                Banquet tickets ({symbol}
                {(catalog.banquet.pricePence / 100).toFixed(0)} each)
              </span>
              <input
                type="number"
                min={0}
                max={20}
                className={fieldClass(false)}
                value={banquetQuantity}
                onChange={(e) =>
                  setBanquetQuantity(Math.max(0, parseInt(e.target.value, 10) || 0))
                }
              />
            </label>
          </div>
        )}

        {banquetQuantity > 0 && (
          <div className="border border-slate-200 p-4 bg-slate-50 text-sm">
            <p className="font-semibold mb-2">Price summary</p>
            <p>
              Banquet x{banquetQuantity}: {symbol}
              {(banquetTotalPence / 100).toFixed(2)}
            </p>
            <p className="font-semibold mt-2">
              Total: {symbol}
              {(totalPence / 100).toFixed(2)}
            </p>
          </div>
        )}

        {error && <ErrorAlert>{error}</ErrorAlert>}

        {catalog.banquet && (
          <button
            type="submit"
            className={primaryBtnClass}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? <Spinner /> : "Proceed to payment"}
          </button>
        )}
      </form>
    </div>
  );
}
